import { useEffect, useRef, useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add,
  Start
} from "@mui/icons-material";
import dayjs from "dayjs";
import { RemoveButton } from "../modals";
import { LatLng, Polygon } from "leaflet";
import { generateRandomHash, useWindowSize } from "../../core";
import { DatePicker } from "@mui/x-date-pickers";
import { AreaSirene, Sirene, SireneData } from "../zas";
import { Municipio, SelectMunicipio } from "../estadoMunicipio";
import { GeometryEditorButton, googleHibrido, HidroLayer, HidroLayerGroup } from "../mapa";

interface SirenesTableProps {
  isAdd?: boolean;
  isEditable?: boolean;
  initSirenes?: Sirene[];
  onChoose?: (sirene: Sirene) => void;
  onChange?: (sirenes: Sirene[]) => void;
}

export const SirenesTable: React.FC<SirenesTableProps> = ({
  onChange,
  onChoose,
  isAdd,
  isEditable,
  initSirenes,
}) => {
  const { height, width } = useWindowSize();
  const theme = useTheme();

  const divRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const layerMapEdit = useRef<HidroLayerGroup>(new HidroLayerGroup({
    name: "Shapes",
    showing: true,
    layers: [
      new HidroLayer({
        name: "Mapa",
        showing: true,
        layer: googleHibrido(),
      }),
    ]
  }));

  const [size, setSize] = useState({ width: 0, height: 0 });
  const [filterSize, setFilterSize] = useState({ width: 0, height: 0 });
  const [titleSize, setTitleSize] = useState({ width: 0, height: 0 });
  const [keyMapEdit, setKeyMapEdit] = useState(generateRandomHash(8));

  const [sirenes, setSirenes] = useState<Sirene[]>(initSirenes ?? []);
  const [newSirene, setNewSirene] = useState<{
    lat: number | string;
    lng: number | string;
    dataInstalacao: Date;
    municipio?: Municipio;
    areaCobertura?: AreaSirene;
  }>({
    lat: 0,
    lng: 0,
    dataInstalacao: new Date(Date.now()),
  });


  useEffect(() => {
    if (divRef.current) {
      const { width, height } = divRef.current.getBoundingClientRect();
      setSize({ width, height });
    }
    if (filterRef.current) {
      const { width, height } = filterRef.current.getBoundingClientRect();
      setFilterSize({ width, height });
    }
    if (titleRef.current) {
      const { width, height } = titleRef.current.getBoundingClientRect();
      setTitleSize({ width, height });
    }
  }, [divRef, filterRef, titleRef, height, width]);

  useEffect(() => {
    if (
      typeof newSirene.lat === "string" ||
      typeof newSirene.lng === "string" ||
      newSirene.lat === 0 ||
      newSirene.lng === 0
    ) return;

    for (const layer of layerMapEdit.current.layers) {
      if (layer.name === "Sirene") {
        layerMapEdit.current.removeLayer(layer);
      }
    }

    const { lng, municipio, ...dadosAdd } = newSirene;

    layerMapEdit.current.push(new HidroLayer({
      name: "Sirene",
      showing: true,
      style: theme.palette.geometries.sirenes,
      layer: new Sirene(
        new LatLng(newSirene.lat, newSirene.lng),
        {
          ...dadosAdd,
          id: 0,
          idZas: 0,
          lon: lng,
          municipio: municipio?.id ?? 0,
          dataCadastro: new Date(Date.now()),
        } as SireneData,
      )
    }));

    setKeyMapEdit(generateRandomHash(8));

  }, [newSirene]);

  const handleAddSirene = () => {
    if (typeof newSirene.lat === "string" ||
      typeof newSirene.lng === "string" ||
      (newSirene.lat === 0 && newSirene.lng === 0)
    ) return;

    const { lng, municipio, ...dadosAdd } = newSirene;

    const sirene = new Sirene(
      new LatLng(newSirene.lat, newSirene.lng),
      {
        ...dadosAdd,
        id: 0,
        idZas: 0,
        lon: lng,
        municipio: municipio?.id ?? 0,
        dataCadastro: new Date(Date.now()),
      } as SireneData,
      theme.palette.geometries.sirenes,
    );

    const newSirenes = [...sirenes];
    newSirenes.push(sirene);
    if (onChange) onChange(newSirenes);
    setSirenes(newSirenes);
    setNewSirene({ lat: 0, lng: 0, municipio: undefined, dataInstalacao: new Date(Date.now()) });
  };

  const handleAddAreaCobertura = (layers: HidroLayerGroup) => {
    const polygon = getPolygon(layers.layers);
    if (!polygon) return;
    newSirene.areaCobertura = new AreaSirene(
      (polygon.layer as Polygon).getLatLngs(),
      theme.palette.geometries.areaCoberturaSirene,
    );
  };

  const onRemove = (unidade: Sirene) => {
    const newSirene = sirenes.filter((ed) => ed !== unidade);
    setSirenes([...newSirene]);
    if (onChange) onChange(newSirene);
  };

  return <Box
    ref={divRef}
    sx={{
      flex: 1,
      width: "100%",
      height: "100%",
      padding: "15px",
      display: "flex",
      alignItems: "center",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    {/* Título */}
    <Box
      ref={titleRef}
      sx={{
        width: "100%",
        paddingTop: "7px",
        paddingX: "15px",
        display: "flex",
        flexDirection: "row",
        alignItems: "start",
        justifyContent: "start",
      }}>
      <Typography fontSize={18} fontWeight={"bold"}>
        {"Sirenes"}
      </Typography>
    </Box>

    {/* Nova Sirene*/}
    <div
      ref={filterRef}
      style={{ width: "100%" }}
    >
      <Box sx={{
        flex: 1,
        paddingX: "15px",
        gap: "15px",
        display: "flex",
        flexDirection: "column",
      }}>

        {/* Nova Sirene */}
        {isAdd &&
          <Box sx={{
            gap: "15px",
            padding: "15px",
            display: "flex",
            flexDirection: "row",
          }}>

            {/* Lat */}
            <TextField
              label="Latitude"
              name="lat"
              value={newSirene.lat.toString().replaceAll(".", ",")}
              onChange={(event) => {
                const strVal = event.target.value.replaceAll(",", ".");

                let val: number | string = strVal;
                if (strVal !== "" && strVal !== "-" && !strVal.endsWith(".")) {
                  val = Number(strVal);
                  if (isNaN(val)) return;
                }
                setNewSirene({
                  ...newSirene,
                  lat: val,
                });
              }}
              variant="standard"
            />

            {/* Lng */}
            <TextField
              label="Longitude"
              name="lng"
              value={newSirene.lng.toString().replaceAll(".", ",")}
              onChange={(event) => {
                const strVal = event.target.value.replaceAll(",", ".");

                let val: number | string = strVal;
                if (strVal !== "" && strVal !== "-" && !strVal.endsWith(".")) {
                  val = Number(strVal);
                  if (isNaN(val)) return;
                }
                setNewSirene({
                  ...newSirene,
                  lng: val,
                });
              }}
              variant="standard"
            />

            {/* Munícipio */}
            <SelectMunicipio
              variant="contained"
              municipio={newSirene.municipio}
              onChoose={(municipio) => setNewSirene({
                ...newSirene, municipio
              })}
            />

            {/* Data de instalação */}
            <DatePicker
              label="Data de Instalção"
              value={dayjs(newSirene.dataInstalacao)}
              onChange={(date) => setNewSirene({
                ...newSirene,
                dataInstalacao: date?.toDate() ?? new Date(Date.now())
              })}
            />

            {/* Selecionar Geometria */}
            <GeometryEditorButton
              key={keyMapEdit}
              variant="contained"
              onSelect={handleAddAreaCobertura}
              startGeometry={layerMapEdit.current}
            >
              Área de Cobertura
            </GeometryEditorButton>

            <div style={{ flex: 1 }} />

            {/* BTN Add */}
            <Tooltip title={"Adicionar Sirene"}>
              <IconButton onClick={handleAddSirene}>
                <Add />
              </IconButton>
            </Tooltip>

          </Box>
        }
      </Box>

      <div style={{ height: "15px" }} />
      <Divider />
      <div style={{ height: "15px" }} />
    </div>

    {/* Tabela de estações */}
    <TableContainer sx={{
      flex: 1,
      overflow: "auto",
      height: size.height - filterSize.height - titleSize.height,
      width: size.width
    }}>
      <Table
        stickyHeader
        size="small"
        aria-label="sticky table"
      >
        <TableHead>
          <TableRow>
            {onChoose && <TableCell align="left">Selecionar</TableCell>}
            <TableCell align="left">Nome</TableCell>
            <TableCell align="left">Latitude</TableCell>
            <TableCell align="left">Longitude</TableCell>
            {isEditable && <TableCell align="center">Remover</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {sirenes.map((row, i) => (
            <TableRow
              key={i}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              {onChoose && <TableCell align="left">
                <IconButton onClick={() => onChoose(row)} >
                  <Start />
                </IconButton>
              </TableCell>}
              <TableCell align="left" sx={{ textOverflow: "ellipsis" }} >{i}</TableCell>
              <TableCell align="left" sx={{ textOverflow: "ellipsis" }} >{row.getLatLng().lat.toString().replaceAll(".", ",")}</TableCell>
              <TableCell align="left" sx={{ textOverflow: "ellipsis" }} >{row.getLatLng().lng.toString().replaceAll(".", ",")}</TableCell>
              {isEditable &&
                <TableCell align="center" sx={{ textOverflow: "ellipsis" }}>
                  <RemoveButton
                    title="Remover Sirene?"
                    message="Deseja mesmo remover esta sirene? Esta ação é irreversível!"
                    onRemove={() => onRemove(row)}
                  />
                </TableCell>
              }
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>;
};


function getPolygon(layers: (HidroLayer | HidroLayerGroup)[]): HidroLayer | undefined {
  for (const layer of layers) {
    if (layer instanceof HidroLayerGroup) {
      const lay = getPolygon(layer.layers);
      if (lay) return lay;
      continue;
    }

    if (layer.layer instanceof Polygon) {
      return layer;
    }
  }

  return undefined;
}
